import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION, trainerReadSideEffectsSchema } from './trainer-event-read'

export const TRAINER_OPS_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerOpsReadQuerySchema = z.object({
  orgId: optionalQueryString,
  limit: optionalQueryString,
  type: optionalQueryString,
  status: optionalQueryString,
  source: optionalQueryString,
  module: optionalQueryString,
  severity: optionalQueryString,
}).passthrough()

const scalarAnswerSchema = z.union([z.string(), z.number().finite(), z.null()])
const customAnswersSchema = z.record(scalarAnswerSchema)
const readSideEffectsSchema = trainerReadSideEffectsSchema.passthrough()

export const trainerOpsNotificationSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  userId: z.string().min(1),
  sourceEntityType: z.string(),
  sourceEntityId: z.string(),
  notificationType: z.string(),
  title: z.string(),
  body: z.string(),
  status: z.string(),
  readAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerOpsNotificationEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('trainer_notifications')]),
  projection: z.literal('trainer-notifications'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerOpsNotificationListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  notifications: z.array(trainerOpsNotificationSummarySchema),
  unreadCount: z.number().int().nonnegative(),
  notificationEvidence: trainerOpsNotificationEvidenceSchema,
  sideEffects: readSideEffectsSchema.optional(),
}).passthrough()

export const trainerOpsHistoricalFeedbackSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  eventId: z.string().min(1),
  createdByUserId: z.string().min(1),
  submittedByUserId: z.string().min(1),
  feedbackType: z.enum(['trainer', 'content']),
  rating: z.number().finite().nullable(),
  comment: z.string(),
  customAnswers: customAnswersSchema,
  status: z.string(),
  source: z.string(),
  active: z.boolean(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  readOnly: z.boolean(),
  origin: z.string().nullable(),
  workshopName: z.string().nullable(),
  certificateDate: z.string().nullable(),
  location: z.string().nullable(),
}).passthrough()

export const trainerOpsHistoricalFeedbackEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_certificate_submissions'),
    z.literal('trainer_xano_source_records'),
  ]),
  projection: z.literal('trainer-certificate-feedback-history'),
  rawPayloadReturned: z.literal(false),
  rawParticipantEmailReturned: z.literal(false),
  rawUidReturned: z.literal(false),
  providerPayloadReturned: z.literal(false),
}).passthrough()

export const trainerOpsFeedbackHistoryResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  feedback: z.array(trainerOpsHistoricalFeedbackSummarySchema),
  feedbackEvidence: trainerOpsHistoricalFeedbackEvidenceSchema,
  sideEffects: readSideEffectsSchema,
}).passthrough()

export const trainerOpsResourceDownloadSummarySchema = z.object({
  id: z.string().min(1),
  resourceFileId: z.string().nullable(),
  resourceFileName: z.string().nullable(),
  resourceFileUrl: z.string().nullable(),
  name: z.string().nullable(),
  viewerUserId: z.string().nullable(),
  viewerResolved: z.boolean(),
  active: z.boolean(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const trainerOpsResourceReferenceSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
}).passthrough()

const trainerOpsResourceEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_resources'),
    z.literal('trainer_resource_files'),
    z.literal('trainer_resource_downloads'),
  ]),
  projection: z.enum(['trainer-resource-download-history', 'trainer-resource-link-audit']),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerOpsResourceDownloadsResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  resource: trainerOpsResourceReferenceSchema,
  downloads: z.array(trainerOpsResourceDownloadSummarySchema),
  resourceEvidence: trainerOpsResourceEvidenceSchema,
  sideEffects: readSideEffectsSchema,
}).passthrough()

export const trainerOpsResourceFileLinkHealthSchema = z.object({
  status: z.enum(['ready', 'missing_url', 'invalid_url', 'unsupported_protocol']),
  openable: z.boolean(),
  reason: z.string(),
  urlReturned: z.boolean(),
  httpProbePerformed: z.literal(false),
  signedPrivateDownloadAvailable: z.boolean(),
  uploadMutationAvailable: z.boolean(),
}).passthrough()

export const trainerOpsResourceLatestLinkProbeSchema = z.object({
  id: z.string().min(1),
  resourceFileId: z.string().min(1),
  urlHost: z.string().nullable(),
  urlProtocol: z.string().nullable(),
  status: z.string(),
  httpStatus: z.number().int().nullable(),
  errorReason: z.string().nullable(),
  durationMs: z.number().finite(),
  rawUrlReturned: z.literal(false),
  headersReturned: z.literal(false),
  bodyReturned: z.literal(false),
  createdAt: z.string().min(1),
}).passthrough()

export const trainerOpsResourceLinkAuditFileSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  url: z.string().nullable(),
  linkHealth: trainerOpsResourceFileLinkHealthSchema,
  latestProbe: trainerOpsResourceLatestLinkProbeSchema.nullable(),
}).passthrough()

export const trainerOpsResourceLinkAuditResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  resource: trainerOpsResourceReferenceSchema,
  files: z.array(trainerOpsResourceLinkAuditFileSchema),
  summary: z.object({
    totalFiles: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
    missingUrl: z.number().int().nonnegative(),
    invalidUrl: z.number().int().nonnegative(),
    unsupportedProtocol: z.number().int().nonnegative(),
    latestProbeAt: z.string().nullable(),
    storedProbeCount: z.number().int().nonnegative(),
    latestReachable: z.number().int().nonnegative(),
    latestUnreachable: z.number().int().nonnegative(),
    latestBlocked: z.number().int().nonnegative(),
    latestError: z.number().int().nonnegative(),
    httpProbePerformed: z.literal(false),
    signedPrivateDownloadAvailable: z.literal(false),
    uploadMutationAvailable: z.literal(false),
  }).passthrough(),
  resourceEvidence: trainerOpsResourceEvidenceSchema.omit({ tablesRead: true }).extend({
    tablesRead: z.tuple([
      z.literal('trainer_resources'),
      z.literal('trainer_resource_files'),
    ]),
  }),
  sideEffects: readSideEffectsSchema,
}).passthrough()

export const trainerOpsDataQualityModuleSchema = z.enum([
  'events',
  'channels',
  'resources',
  'invoices',
  'affiliates',
  'trainers_grants',
  'certificates',
  'feedback',
  'group_work',
  'training_centre',
])
export const trainerOpsDataQualitySeveritySchema = z.enum(['critical', 'high', 'medium', 'low'])
export const trainerOpsDataQualityStatusSchema = z.enum(['open', 'triaged', 'in_progress', 'resolved', 'archived', 'gated', 'unavailable'])

export const trainerOpsDataQualityExceptionSchema = z.object({
  key: z.string().min(1),
  module: trainerOpsDataQualityModuleSchema,
  title: z.string(),
  severity: trainerOpsDataQualitySeveritySchema,
  status: trainerOpsDataQualityStatusSchema,
  ownerUserId: z.string().nullable(),
  sourceCategory: z.string(),
  affectedRecordType: z.string(),
  affectedRecordId: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  evidenceRefs: z.array(z.string()),
  remediationLedgerId: z.string().nullable(),
  notes: z.string().nullable(),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerOpsDataQualityResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  filters: z.object({
    module: z.union([trainerOpsDataQualityModuleSchema, z.literal('all')]),
    severity: z.union([trainerOpsDataQualitySeveritySchema, z.literal('all')]),
    status: z.union([trainerOpsDataQualityStatusSchema, z.literal('all')]),
  }).passthrough(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    bySeverity: z.record(z.number().int().nonnegative()),
    byStatus: z.record(z.number().int().nonnegative()),
    modules: z.array(z.object({
      module: trainerOpsDataQualityModuleSchema,
      total: z.number().int().nonnegative(),
      open: z.number().int().nonnegative(),
      critical: z.number().int().nonnegative(),
      high: z.number().int().nonnegative(),
      lastSeenAt: z.string().nullable(),
    }).passthrough()),
  }).passthrough(),
  exceptions: z.array(trainerOpsDataQualityExceptionSchema),
  dataQualityEvidence: z.object({
    sourceOfTruth: z.literal('DB1'),
    tablesRead: z.array(z.string()),
    projection: z.literal('trainer-data-quality-dashboard'),
    rawPayloadReturned: z.literal(false),
  }).passthrough(),
  sideEffects: z.object({
    db1Read: z.boolean(),
    db1Written: z.literal(false),
    auditWritten: z.literal(false),
    providerTouched: z.literal(false),
    billingTouched: z.literal(false),
    eventEmitted: z.literal(false),
    uploadTouched: z.literal(false),
    llmTouched: z.literal(false),
    queuePublished: z.literal(false),
    db2Touched: z.literal(false),
    deployLiveTraffic: z.literal(false),
    destructiveCleanup: z.literal(false),
  }).passthrough(),
}).passthrough()

const trainerOpsGroupWorkStatusSchema = z.enum(['submitted', 'reviewed', 'archived'])
const trainerOpsGroupWorkSourceSchema = z.enum(['owned_public_intake', 'migration', 'admin', 'trainer_app'])

export const trainerOpsGroupWorkExportResponseSchema = z.object({
  contract_version: z.literal(TRAINER_OPS_READ_CONTRACT_VERSION),
  export: z.object({
    filename: z.string().min(1),
    mimeType: z.literal('text/csv'),
    rowCount: z.number().int().nonnegative(),
    csv: z.string(),
    filters: z.object({
      status: trainerOpsGroupWorkStatusSchema.nullable(),
      source: trainerOpsGroupWorkSourceSchema.nullable(),
    }).passthrough(),
  }).passthrough(),
  groupWorkEvidence: z.object({
    sourceOfTruth: z.literal('DB1'),
    tablesRead: z.tuple([
      z.literal('trainer_events'),
      z.literal('trainer_group_work_responses'),
    ]),
    projection: z.literal('trainer-group-work-responses'),
    rawPayloadReturned: z.literal(false),
    providerTouched: z.literal(false),
  }).passthrough(),
  sideEffects: readSideEffectsSchema,
}).passthrough()

export type TrainerOpsReadQuery = z.infer<typeof trainerOpsReadQuerySchema>
export type TrainerOpsNotificationSummary = z.infer<typeof trainerOpsNotificationSummarySchema>
export type TrainerOpsNotificationListResponse = z.infer<typeof trainerOpsNotificationListResponseSchema>
export type TrainerOpsHistoricalFeedbackSummary = z.infer<typeof trainerOpsHistoricalFeedbackSummarySchema>
export type TrainerOpsFeedbackHistoryResponse = z.infer<typeof trainerOpsFeedbackHistoryResponseSchema>
export type TrainerOpsResourceDownloadSummary = z.infer<typeof trainerOpsResourceDownloadSummarySchema>
export type TrainerOpsResourceDownloadsResponse = z.infer<typeof trainerOpsResourceDownloadsResponseSchema>
export type TrainerOpsResourceLinkAuditResponse = z.infer<typeof trainerOpsResourceLinkAuditResponseSchema>
export type TrainerOpsDataQualityException = z.infer<typeof trainerOpsDataQualityExceptionSchema>
export type TrainerOpsDataQualityResponse = z.infer<typeof trainerOpsDataQualityResponseSchema>
export type TrainerOpsGroupWorkExportResponse = z.infer<typeof trainerOpsGroupWorkExportResponseSchema>
